<template>
    <div class="autocomplete-wrapper">
        <SiteInput :class="[{'focused': searchFocused}]" @keydown="searchFocused = true" name="selectitem" :label="placeholder"
                @keydown.up="positionUp" @keydown.down="positionDown" autocomplete="off"
                @keydown.enter.prevent="addItem" @keydown.esc="searchFocused = false" v-model="searchTerm"
                @click="searchFocused = true" ref="inputBox" v-dropdown></SiteInput>
        <div v-if="searchFocused" id="autocomplete-options" class="dropdown-menu autocomplete">
            <a href="#" :class="{'highlight': index === position}" v-for="(item, index) in itemsList"
                :key="index" @click.prevent="selected(item)">
            {{ item.name }}
            </a>
            <div class="clearfix"></div>
        </div>
        <div v-if="selectedItems.length" class="autocomplete-tags">
            <div v-for="(item, index) in selectedItems" :key="index">
                {{ item.name }}<i class="fa fa-times site-color" @click.prevent="unselected(item)"></i>
            </div>
        </div>
    </div>
</template>
<script>
import dropdownDirective from '../directive/dropdown';
import SiteInput from 'Site/component/SiteInput.vue';

export default {
    name: 'AutoComplete',
    components: {
        SiteInput
    },
    computed: {
        itemsList() {
            // Apply searchTerm filter
            // && filter out already selected items
            return this.allItems.filter(item => item.name.toLowerCase().indexOf(this.searchTerm.toLowerCase()) !== -1
                && this.selectedItems.indexOf(item) === -1);
        }
    },
    directives: {
        dropdown: dropdownDirective
    },
    data() {
        return {
            position: -1,
            searchFocused: false,
            searchTerm: '',
            selectedItems: this.value,
            storeFilterFlag: false
        };
    },
    methods: {
        addItem() {
            if ((this.position === -1 || !this.itemsList.length) && this.searchTerm) {
                const setSelectedViaSearch = this.allItems.filter(item => item.name === this.searchTerm);
                if (setSelectedViaSearch.length) {
                    this.selected(setSelectedViaSearch);
                } else if (this.allowAddingNonMatchingItems && this.checkMax()) {
                    if (this.selectedItems.filter(item => item.name === this.searchTerm).length) {
                        return false;
                    }
                    const item = { name: this.searchTerm, value: `non_match_${this.searchTerm}` };
                    this.selectedItems.push(item);
                    this.searchTerm = '';
                    this.searchFocused = false;
                    this.storeFilter();
                }
            } else if (this.position !== -1) {
                this.selected(this.itemsList[this.position]);
            }
            this.position = -1;
            return false;
        },
        selected(item) {
            if (!this.checkMax() || this.allItems.map(checkItem => checkItem.name).indexOf(item.name) === -1) {
                return;
            }
            if (this.single) {
                this.selectedItems = [item];
            } else {
                this.selectedItems.push(item);
            }
            this.searchTerm = '';
            this.searchFocused = false;
            this.storeFilter();
        },
        checkMax() {
            const check = this.max && this.selectedItems.length + 1 > this.max;
            if (check) {
                this.$emit('max');
            }
            return !check;
        },
        /** Triggered when selected items is updated
         * @event selected-items
         * @type {Object}
         */
        storeFilter() {
            const storeValues = [];
            this.selectedItems.forEach((item) => {
                storeValues.push(item.id);
            });
            if (this.filterName) {
                this.storeFilterFlag = true;
                this.$emit('selected-items', { key: this.filterName, value: storeValues.join(',') });
            } else {
                this.$emit('selected-items', this.selectedItems);
            }
        },
        positionDown() {
            this.position = this.position < this.itemsList.length - 1 ? this.position + 1 : this.position;
        },
        positionUp() {
            this.position = this.position > -1 ? this.position - 1 : -1;
        },
        formatSetFromRouter(routerItems) {
            this.selectedItems = [];
            const itemIds = routerItems.split(',');
            // check against allItems to build selectedItems list
            this.allItems.forEach((item, index) => {
                itemIds.forEach((id) => {
                    if (item.id === parseInt(id, 10)) {
                        this.selectedItems.push(this.allItems[index]);
                    }
                });
            });
        },
        /** Triggered when a selected item is removed
         * @event hideSubmit
         * @type {null}
         */
        unselected(item) {
            this.selectedItems = this.selectedItems.filter(selected => selected !== item);
            if (this.selectedItems.length < 1) {
                this.$parent.$emit('hideSubmit');
            }
            this.storeFilter();
        }
    },
    watch: {
        position() {
            this.$nextTick(() => {
                if (this.searchFocused) {
                    document.getElementById('autocomplete-options').scrollTop = this.position >= 0 ? this.position * 34 : 0;
                }
            });
        },
        searchTerm() {
            this.position = 0;
        },
        routerItems(routerItems) {
            // incoming router items are ids only - need to format filter to include name when setting as selectedItems
            if (!this.storeFilterFlag) {
                this.formatSetFromRouter(routerItems);
            }
            this.storeFilterFlag = false;
        }
    },
    props: {
        routerItems: {
            type: String,
            default: null
        },
        single: {
            type: Boolean,
            default: false
        },
        filterName: {
            type: String,
            default: null
        },
        allowAddingNonMatchingItems: {
            type: Boolean,
            default: false
        },
        allItems: {
            // [{name: String, value: Number}, {}..]
            type: Array,
            required: true
        },
        max: {
            type: Number,
            required: false,
            default: 0
        },
        placeholder: {
            type: String,
            required: true
        },
        value: {
            type: Array,
            required: false,
            default() {
                return [];
            }
        }
    }
};

</script>

<style lang="scss">
@import "../scss/variables";

.autocomplete {
    max-height: 300px;
    width: 100%;
    overflow-y: scroll;
    border: 1px solid #ECECEC;
    list-style: none;
    padding: 0;
    position: absolute;
    z-index: 101;
    background-color: #fff;
    a {
        padding: 8px 10px 8px 8px;
        display: inline-block;
        width: 100%;
    }
    > a:nth-of-type(even) {
        background-color: #f5f5f5;
    }
    a.highlight, a:hover {
        background-color: $text-color;
        text-decoration: none;
        color: $navbar-inverse-link-color;
    }
    &.dropdown-menu {
        top: auto;
    }
}

.autocomplete-wrapper {
    .form-group {
        margin: 0;
    }
}

.autocomplete-tags {
    padding: 0;
    margin: 7px 0;
    li, div {
        display: inline-block;
        margin: 2px;
        background-color: #ECECEC;
        padding: 3px;
        border-radius: 5px;
        border: 1px solid #DEDEDE;
        i {
            margin-left: 5px;
            &:hover {
            cursor: pointer;
            }
        }
    }
}
</style>
